import { ProjectServiceType, ProjectStatus, ProjectServiceResponse, ProjectResponse } from '@business/entities/project';
import { R } from '@core/utils/r';
import { InvoiceDiscountType } from '@business/entities/TransactionInvoice';
import { InvoiceFromData, ProjectLineItemData, ProposalDiscountFormData } from '@modules/projects/CreateProjectPage/ProjectForm';
import { Formatter } from '@core/utils/formatter';
import { InvoiceSalesTaxData } from '@modules/common/InvoiceSalesTax';
import { ProposalDiscountCategory } from '@business/entities/proposal';
import { TaxService } from '../tax_service';

export function getStatusTitle(status: ProjectStatus) {
  switch (status) {
    case ProjectStatus.pending:
      return 'Pending Setup';
    case ProjectStatus.active:
      return 'Active';
    case ProjectStatus.completed:
      return 'Completed';
    case ProjectStatus.cancelled:
      return 'Canceled';
    case ProjectStatus.all:
      return 'All';
    case ProjectStatus.overdue:
      return 'Overdue';
  }
  return '';
}

export function getStatusClass(status: ProjectStatus) {
  switch (status) {
    case ProjectStatus.pending:
      return 'badge-soft-info';
    case ProjectStatus.active:
      return 'badge-soft-primary';
    case ProjectStatus.completed:
      return 'badge-soft-success';
    case ProjectStatus.cancelled:
      return 'badge-soft-danger';
  }
}

export function getServiceTypes() {
  return [ProjectServiceType.project, ProjectServiceType.marketing, ProjectServiceType.recurring];
}

export function getServiceTypeTitle(type: ProjectServiceType, combined = false) {
  switch (type) {
    case ProjectServiceType.project:
      return 'Project';
    case ProjectServiceType.marketing:
      return combined ? 'Subscriptions' : 'Subscription on Subscription Board';
    case ProjectServiceType.recurring:
      return combined ? 'Subscriptions' : 'Subscription';
    case ProjectServiceType.inactive:
      return 'Inactive';
  }
}

export function getRStatuses() {
  return [ProjectStatus.active, ProjectStatus.completed, ProjectStatus.overdue, ProjectStatus.cancelled, ProjectStatus.all];
}

export function filterByCurrency(services: ProjectServiceResponse[], currency?: string) {
  if (!currency) return services;
  return services.filter(service => (service.currency || '').toLowerCase() === currency.toLowerCase() || R.isEmpty(service.currency));
}

export function getInvoiceTotal(invoiceFormValue: InvoiceFromData) {
  // const subTotal = invoiceFormValue.lineItems.reduce((total, line) => total + line.qty * line.rate, 0);

  const subTotal = allLineItemsTotal(invoiceFormValue.lineItems);

  const discount = () => {
    if (!invoiceFormValue.discount.isDiscount) return 0;
    if (invoiceFormValue.discount.type === InvoiceDiscountType.fixed) {
      return invoiceFormValue.discount.value;
    } else {
      return (invoiceFormValue.discount.value * subTotal) / 100;
    }
  };

  const discountAmount = discount();
  const totalAfterDiscount = subTotal - discountAmount;

  const salesTax = () => {
    if (!invoiceFormValue.salesTax || !invoiceFormValue.salesTax.isSalesTax || invoiceFormValue.salesTax.value === 0) {
      return 0;
    }
    return getTaxAmt(R.cloneDeep(invoiceFormValue.lineItems), invoiceFormValue.salesTax, invoiceFormValue.discount);
  };

  const salesTaxAmount = salesTax();

  return totalAfterDiscount + salesTaxAmount;
}

export function getTimeTrackerColor(project: ProjectResponse) {
  const hoursTracked = (Formatter.formatSeconds(project.totalTimeTracked, 'h') as number) ?? 0;
  const minutesTracked = (Formatter.formatSeconds(project.totalTimeTracked, 'm') as number) ?? 0;
  const totalTime = project.hours;

  if (!totalTime) {
    return 'badge-soft-grey';
  }

  if (hoursTracked < totalTime) {
    return 'badge-soft-success';
  }

  if (hoursTracked > totalTime) {
    return 'badge-soft-danger';
  }

  if (hoursTracked === totalTime && minutesTracked > 0) {
    return 'badge-soft-danger';
  }

  return 'badge-soft-grey';
}

export function getTimeTrackedTooltip(project: ProjectResponse) {
  const totalTime = project.hours;

  const t = Formatter.formatSeconds(project.totalTimeTracked);
  if (!totalTime) {
    return `This project has ${t} of time tracked. No number of hours have seen set for this project.`;
  }

  return `This project has ${t} of time tracked out of ${totalTime} hours.`;
}

export function lineTotal(item: ProjectLineItemData): number {
  if (!item) return 0;
  if (item.packageItems.length > 0) {
    return item.packageItems.reduce((total, line) => R.roundDecimal(total + R.toNumber(lineTotal(line)), 2), 0);
  } else {
    return R.roundDecimal(item.qty * item.rate, 2);
  }
}

export function allLineItemsTotal(items: ProjectLineItemData[]): number {
  return items.reduce((total, item) => R.roundDecimal(total + lineTotal(item), 2), 0);
}

export function totalLineQty(items: ProjectLineItemData): number {
  if (!items) return 0;
  if (items.packageItems.length > 0) {
    return items.packageItems.reduce((total, line) => R.roundDecimal(total + R.toNumber(totalLineQty(line)), 2), 0);
  } else {
    return R.toNumber(items.qty);
  }
}

export function totalLineRate(items: ProjectLineItemData): number {
  if (!items) return 0;
  if (items.packageItems.length > 0) {
    return items.packageItems.reduce((total, line) => R.roundDecimal(total + R.toNumber(totalLineRate(line)), 2), 0);
  } else {
    return items.rate;
  }
}

export function getSubTotalNumber(formValue: ProjectLineItemData): number {
  if (!formValue) return 0;
  if (formValue.packageItems && formValue.packageItems.length > 0) {
    return formValue.packageItems.reduce((acc, item) => {
      const total = acc + item.qty * item.rate;
      return R.isNaN(total) ? 0 : total;
    }, 0);
  } else {
    const total = formValue.qty * formValue.rate;
    return R.isNaN(total) ? 0 : total;
  }
}

export function discountableRows(items: ProjectLineItemData[]): ProjectLineItemData[] {
  return items.map(item => {
    if (item.packageItems && item.packageItems.length > 0) {
      return { ...item, packageItems: item.packageItems.filter(() => true) };
    }
    return item;
  });
}

export function calculateGrandTotalDiscount(items: ProjectLineItemData[], editable = true, discount: ProposalDiscountFormData): number {
  const t = items.reduce((acc, item) => acc + getSubTotalNumber(item), 0);
  return getDiscountAmount(discount, t);
}

export function getDiscountAmount(discount: ProposalDiscountFormData, amount: number): number {
  if (discount.type === InvoiceDiscountType.percentage) {
    return (amount * discount.value) / 100;
  } else {
    return discount.value ?? 0;
  }
}

export function getTaxAmt(items: ProjectLineItemData[], salesTax: InvoiceSalesTaxData, discount: ProposalDiscountFormData) {
  const allLineItems = items;
  const allPackageItems = R.flatten(allLineItems.map(l => l.packageItems));
  let taxableItems = [
    ...allLineItems.map(o => {
      return { ...o, packageItems: [] };
    }),
    ...allPackageItems.map(o => {
      return { ...o, packageItems: [] };
    }),
  ];

  taxableItems = taxableItems.filter(l => {
    if (!l.service) return true;
    return !l.service.includes('package');
  });

  const parsedItems = taxableItems.map(l => ({ taxable: l.taxable, subtotal: getSubTotalNumber(l) }));

  let discountAmt = 0;
  if (discount.isDiscount) {
    const discountType = discount.type;
    const discValue = discount.value;
    if (discountType === 'fixed') {
      discountAmt = R.toNumber(discValue);
    } else {
      const filteredData = discountableRows(R.cloneDeep(items));
      discountAmt = calculateGrandTotalDiscount(filteredData, true, discount);
    }
    discount = {
      ...discount,
      type: InvoiceDiscountType.fixed,
      value: discountAmt,
    };
  }

  return TaxService.totalTaxAmt(parsedItems, discount as any, salesTax, true);
}
